import { makeStyles } from '@material-ui/core';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { useTrigger } from '@/components/modules/Application/components/Cart/DefaultCart/components/shared/hooks/useTrigger';
import { AbstractLoanPaymentMethod } from '@/components/modules/Application/components/PaymentMethod/_shared/entities/PaymentMethod/AbstractLoanPaymentMethod';
import { usePaymentMethods } from '@/components/modules/Application/components/PaymentMethod/_shared/hooks/usePaymentMethods';
import { Font } from '@/components/shared/components/Font';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { useKeyboardActions } from '@/components/shared/hooks/useKeyboardActions';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface TermValueFieldProps<T extends AbstractLoanPaymentMethod> {
  paymentMethodEntity: T;
  handleClose: () => void;
}

export const TermValueField = <T extends AbstractLoanPaymentMethod>({
  paymentMethodEntity,
  handleClose,
}: TermValueFieldProps<T>) => {
  const { updatePaymentMethodEntity } = usePaymentMethods();
  const { getTextIdTranslation } = useTranslation();
  const { watch, setError, clearErrors, setValue } = useFormContext();
  const { palette, customBorderRadius } = useCustomTheme();
  const { trigger: setTermNotAllowedError } = useTrigger({
    callback: () =>
      setError('term', {
        type: 'manual',
        message: `${getTextIdTranslation('VALUE_MUST_BE_BETWEEN')} ${minAvailableTerm} ${getTextIdTranslation(
          'LOWERCASE_AND',
        )} ${maxAvailableTerm} ${getTextIdTranslation('MONTHS')}`,
      }),
  });

  useKeyboardActions({
    enterAction: () => {
      handleClose();
      setValue('term', paymentMethodEntity.selectedTerm);
    },
  });

  const termWatcher = Number(watch('term'));
  const minAvailableTerm = paymentMethodEntity.availableTerms[0];
  const maxAvailableTerm = paymentMethodEntity.availableTerms[paymentMethodEntity.availableTerms.length - 1];
  const isTermNotAllowed = termWatcher < minAvailableTerm || termWatcher > maxAvailableTerm;

  useEffect(() => {
    if (isTermNotAllowed) {
      return setTermNotAllowedError();
    }
    clearErrors();
    if (termWatcher !== paymentMethodEntity.selectedTerm) {
      updatePaymentMethodEntity(paymentMethodEntity.updateSelectedTerm(Number(termWatcher)));
    }
  }, [termWatcher]);

  useEffect(() => {
    if (paymentMethodEntity.selectedTerm !== termWatcher) setValue('term', paymentMethodEntity.selectedTerm);
  }, [paymentMethodEntity.selectedTerm]);

  const useStyles = makeStyles({
    root: {
      '& .MuiInputBase-root': {
        backgroundColor: palette.grey[100],
        paddingTop: 2,
        marginBottom: 0,
        borderRadius: customBorderRadius.paper,
      },
      '& .MuiInputBase-input': {
        paddingBottom: 0,
      },
      '& .MuiInputBase-root::before': {
        borderBottom: 'none !important',
      },
      '& .MuiInputBase-root.Mui-focused::before': {
        borderBottom: 'none !important',
      },
      '& .MuiInputBase-root.Mui-error::before': {
        borderBottom: 'none !important',
      },
      '& .MuiInputBase-root::after': {
        borderBottom: 'none !important',
      },
      '& .MuiInputBase-root.Mui-focused::after': {
        borderBottom: 'none !important',
      },
      '& .MuiInputBase-root.Mui-error::after': {
        borderBottom: 'none !important',
      },
    },
  });

  const classes = useStyles();
  return (
    <TextFormField
      autoFocus
      className={classes.root}
      type="number"
      name={'term'}
      InputProps={{
        endAdornment: (
          <Font
            variant="body2"
            color="textPrimary"
            style={{ marginBottom: '-14px', marginLeft: '-4px', paddingRight: '8px' }}
          >
            <strong>{getTextIdTranslation('MONTHS')}</strong>
          </Font>
        ),
      }}
    />
  );
};
